import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MoreHorizontal, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchLocations, LocationListing, LocationFilters, createLocation, updateLocation, deleteLocation } from '@/services/locationListingService';
import { LocationFormModal } from '@/components/admin/LocationFormModal';
import { DeleteConfirmationDialog } from '@/components/admin/DeleteConfirmationDialog';

const LocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<LocationListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0, totalPages: 0 });
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Partial<LocationListing> | undefined>(undefined);
  const [deletingLocation, setDeletingLocation] = useState<LocationListing | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchLocations({ search: searchQuery, page: pagination.page, per_page: pagination.per_page });
      console.log('Locations API response:', response);
      setLocations(response.data);
      setPagination(prev => ({ ...prev, total: response.total, totalPages: response.total_pages }));
    } catch (e) {
      console.error('Error fetching locations:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [searchQuery, pagination.page, pagination.per_page]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFormSubmit = async (data: Partial<LocationListing>) => {
    setActionLoading(true);
    try {
      if (data.id) {
        await updateLocation(data.id, data);
      } else {
        await createLocation(data);
      }
      setFormModalOpen(false);
      fetchData();
    } catch (e) {
      // Optionally show error toast
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingLocation) return;
    setActionLoading(true);
    try {
      await deleteLocation(deletingLocation.id);
      setDeleteDialogOpen(false);
      fetchData();
    } catch (e) {
      // Optionally show error toast
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Locations</h1>
            <p className="text-muted-foreground">Manage all locations on your platform.</p>
          </div>
          <Button onClick={() => { setEditingLocation(undefined); setFormModalOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Location Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search locations..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg shadow ring-1 ring-white/[0.05] dark:ring-zinc-800">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-800">
                <thead className="bg-gray-50 dark:bg-zinc-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Country</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">County</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rating</th>
                    <th className="w-[50px]"></th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-800">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Loading...</td>
                    </tr>
                  ) : locations.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">No locations found</td>
                    </tr>
                  ) : (
                    locations.map((location) => (
                      <tr key={location.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{location.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {typeof location.country === 'object' && location.country !== null
                            ? ((location.country as any)?.name || (location.country as any)?.code || JSON.stringify(location.country))
                            : location.country}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {typeof location.county === 'object' && location.county !== null
                            ? ((location.county as any)?.name || (location.county as any)?.code || JSON.stringify(location.county))
                            : location.county}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{location.type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{location.rating ?? '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0" onClick={() => { setEditingLocation(location); setFormModalOpen(true); }}>
                            <span className="sr-only">Edit location</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-red-500" onClick={() => { setDeletingLocation(location); setDeleteDialogOpen(true); }}>
                            <span className="sr-only">Delete location</span>
                            &#x2715;
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <LocationFormModal
          open={formModalOpen}
          onOpenChange={setFormModalOpen}
          initialData={editingLocation}
          onSubmit={handleFormSubmit}
        />
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          description={`Are you sure you want to delete "${deletingLocation?.name}"?`}
        />
      </div>
    </AdminLayout>
  );
};

export default LocationsPage; 